import { useCallback, useEffect, useRef, useState } from 'react';

interface UseFilterStateOptions<T> {
  storageKey: string;
  defaults: T;
  onFilterChange: (filters: T) => void;
  debounceMs?: number;
  /**
   * Seeds filters from URL params (e.g. a deep link) on initial mount only.
   * When present, the persisted sessionStorage state is bypassed entirely and
   * the initial state is `{ ...defaults, ...overrides }` — so a deep link lands
   * on a deterministic view instead of inheriting whatever filters were left
   * from a prior session. Ignored on subsequent renders.
   */
  overrides?: Partial<T>;
}

export function useFilterState<T>({
  storageKey,
  defaults,
  onFilterChange,
  debounceMs = 350,
  overrides,
}: UseFilterStateOptions<T>) {
  const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  });

  const [filters, setFilters] = useState<T>(() => {
    if (overrides) {
      return { ...defaults, ...overrides };
    }
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved) as T;
      } catch (e) {
        console.error('Failed to parse saved filters:', e);
      }
    }
    return defaults;
  });

  // Fire once on mount with the initial value (no debounce) so consumers
  // can start fetching immediately.
  useEffect(() => {
    onFilterChangeRef.current(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist + debounced re-emit on every change (also fires once on mount).
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
    const t = setTimeout(() => onFilterChangeRef.current(filters), debounceMs);
    return () => clearTimeout(t);
  }, [filters, storageKey, debounceMs]);

  const clear = useCallback(() => {
    setFilters(defaults);
    // No sessionStorage.removeItem here: the persist effect re-fires the
    // moment filters becomes defaults and writes JSON.stringify(defaults)
    // back. Reading defaults from storage on next mount is equivalent to
    // reading nothing, so the explicit removeItem would be dead code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { filters, setFilters, clear };
}
